<template>
  <div v-if="rendering" class="relative mask-wrapper" :style="maskWrapperCssProps">
    <div class="mask absolute" :style="maskCssProps"/>
    <svg width="640" height="640" :style="svgCssProps">
      <g :transform="translateOffset">
        <image
          x="0"
          y="0"
          :width="imageWidth"
          :height="imageHeight"
          preserveAspectRatio="xMidYMid slice"
          :xlink:href="src"
        />
      </g>
    </svg>
  </div>
</template>

<script>
import { lusolve } from "mathjs";
import round from "lodash/round";
import { contain } from "intrinsic-scale";

// Only ever used to keep the geometry maths finite while the component renders nothing
const FALLBACK_QUAD = [[0, 0], [1, 0], [1, 1], [0, 1]];

function getInnerSize(node) {
  if (node) {
    const style = getComputedStyle(node);
    const {
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom,
      borderLeftWidth,
      borderRightWidth,
      borderTopWidth,
      borderBottomWidth
    } = style;
    const paddingX = parseFloat(paddingLeft) + parseFloat(paddingRight);
    const paddingY = parseFloat(paddingTop) + parseFloat(paddingBottom);

    const borderX = parseFloat(borderLeftWidth) + parseFloat(borderRightWidth);
    const borderY = parseFloat(borderTopWidth) + parseFloat(borderBottomWidth);

    // Element width and height minus padding and border
    const width = node.offsetWidth - paddingX - borderX;
    const height = node.offsetHeight - paddingY - borderY;

    return { width, height };
  }
}

// Find the nearest parent element with non-zero width (go up to 5 levels)
// Retail has a wrapper to load this through react, which had issues finding parent width on the ipad. This successfully found a usable width.
function findNearestParentWidth(element) {
  let current = element;
  let size = getInnerSize(current);

  for (let i = 0; i < 5 && current && (!size || size.width === 0); i++) {
    current = current.parentElement;
    size = current ? getInnerSize(current) : null;
  }

  return size;
}

// A set of points to define the images four corners and goal perspective
function matrixTransformStyle(sourcePoints, targetPoints) {
  const alpha = [];
  const beta = [];
  const pointsCount = sourcePoints.length;
  for (let index = 0; index < pointsCount; ++index) {
    const sourcePoint = sourcePoints[index];
    const targetPoint = targetPoints[index];
    alpha.push([
      sourcePoint[0],
      sourcePoint[1],
      1,
      0,
      0,
      0,
      -sourcePoint[0] * targetPoint[0],
      -sourcePoint[1] * targetPoint[0]
    ]);
    beta.push(targetPoint[0]);
    alpha.push([
      0,
      0,
      0,
      sourcePoint[0],
      sourcePoint[1],
      1,
      -sourcePoint[0] * targetPoint[1],
      -sourcePoint[1] * targetPoint[1]
    ]);
    beta.push(targetPoint[1]);
  }

  const output = lusolve(alpha, beta);
  const matrix = [
    output[0],
    output[3],
    0,
    output[6],
    output[1],
    output[4],
    0,
    output[7],
    0,
    0,
    1,
    0,
    output[2],
    output[5],
    0,
    1
  ].map(function(x) {
    return round(x, 6);
  });

  return `matrix3d(${matrix})`;
}

export default {
  props: {
    frameSpec: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      containerWidth: 0,
      resizeHandler: null
    };
  },
  computed: {
    src() {
      const artwork = this.frameSpec.getArtwork();
      return artwork.customerSpecified && artwork.customerSpecified.url || artwork.croppedAndNormalized && artwork.croppedAndNormalized.url;
    },
    rendering () {
      return this.frameSpec.usableMaskedRendering();
    },
    parsedConfig () {
      return this.rendering ? JSON.parse(this.rendering.config) : null;
    },
    sourcePoints() {
      return this.parsedConfig ? this.parsedConfig.source : FALLBACK_QUAD;
    },
    targetPoints () {
      return this.parsedConfig ? this.parsedConfig.target : FALLBACK_QUAD;
    },
    backgroundUrl () {
      return this.rendering ? this.rendering.image.url : '';
    },
    maskWrapperCssProps() {
      return {
        "--scale": `scale(${this.scale})`
      };
    },
    maskCssProps() {
      return {
        backgroundImage: `url(${this.backgroundUrl})`
      };
    },
    svgCssProps() {
      return {
        transform: this.matrixTransformStyle,
        transformOrigin: this.transformOrigin
      };
    },
    scale() {
      try {
        return this.containerWidth / 640;
      } catch (error) {
        return 1;
      }
    },
    // The third source point shall be the bottom right corner and therefore Width and height
    imageWidth() {
      return this.sourcePoints[2][0];
    },
    imageHeight() {
      return this.sourcePoints[2][1];
    },
    containedImage() {
      return contain(640, 640, this.imageWidth, this.imageHeight);
    },
    transformOrigin() {
      const { x, y } = this.containedImage;
      return `${x}px ${y}px 0px`;
    },
    translateOffset() {
      const { x, y } = this.containedImage;
      return `translate(${x},${y})`;
    },
    matrixTransformStyle() {
      return matrixTransformStyle(this.sourcePoints, this.targetPoints);
    }
  },
  mounted() {
    const size = findNearestParentWidth(this.$el.parentElement);
    this.containerWidth = size?.width || (typeof window !== 'undefined' ? window.innerWidth : 640);

    if (typeof window !== 'undefined') {
      this.resizeHandler = () => {
        const elSize = findNearestParentWidth(this.$el.parentElement);
        this.containerWidth = elSize?.width || window.innerWidth;
      };
      window.addEventListener("resize", this.resizeHandler);
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined' && this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
  },
};
</script>

<style lang="scss">
.mask-wrapper {
  transform: var(--scale);
  transform-origin: top left;

  .mask {
    background-size: cover;
    height: 640px;
    left: 0;
    top: 0;
    width: 640px;
    z-index: 1;
  }
  
  .image {
    z-index: 0;
  }
}
</style>
